import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";

import "../pages/LoginPage.css";
import "./UpdatePasswordPage.css";
import EyeIcon from "../assets/OrgangeEyeIcon.svg";

import LoginImage from "../assets/LoginPageImage.svg";
import Logo from "../assets/LoginVocovoLogo.svg";

/* ------------------------------------------------
   PASSWORD HELPERS
------------------------------------------------ */
const validatePassword = (password) => {
  const pwd = password.trim();

  if (pwd.length < 8)
    return "Password must be at least 8 characters long.";
  if (/\s/.test(pwd))
    return "Password must not contain spaces.";
  if (!/[A-Z]/.test(pwd))
    return "Password must contain at least one uppercase letter.";
  if (!/[a-z]/.test(pwd))
    return "Password must contain at least one lowercase letter.";
  if (!/[0-9]/.test(pwd))
    return "Password must contain at least one number.";
  if (!/[^A-Za-z0-9]/.test(pwd))
    return "Password must contain at least one special character.";

  return "";
};

const getPasswordStrength = (password) => {
  let score = 0;
  if (password.length >= 8) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[a-z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;

  if (score <= 2) return "weak";
  if (score <= 4) return "medium";
  return "strong";
};

const ResetPasswordPage = () => {
  const apiBase = process.env.REACT_APP_API_BASE_URL;
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get("token");

  const [isValidToken, setIsValidToken] = useState(null);
  const [tokenTitle, setTokenTitle] = useState("Invalid Link");
  const [tokenError, setTokenError] = useState("");

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [passwordError, setPasswordError] = useState("");
  const [strength, setStrength] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  /* ------------------------------------------------
     VERIFY TOKEN (GET)
  ------------------------------------------------ */
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setIsValidToken(false);
        setTokenTitle("Invalid Link");
        setTokenError(
          'query parameter "token" is required and must be a non-empty string.'
        );
        return;
      }

      try {
        await axios.get(`${apiBase}/auth/reset-password?token=${token}`);
        setIsValidToken(true);
      } catch (err) {
        const backendMessage =
          err.response?.data?.message || "Invalid reset link.";

        setIsValidToken(false);
        setTokenError(backendMessage);

        if (backendMessage.toLowerCase().includes("expired")) {
          setTokenTitle("Expired Link");
        } else {
          setTokenTitle("Invalid Link");
        }
      }
    };

    verifyToken();
  }, [token, apiBase]);

  /* ------------------------------------------------
     SUBMIT NEW PASSWORD (POST)
  ------------------------------------------------ */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const trimmedPassword = newPassword.trim();
    const trimmedConfirm = confirmPassword.trim();

    if (!trimmedPassword || !trimmedConfirm) {
      setError("Please fill all fields.");
      return;
    }

    const validationError = validatePassword(trimmedPassword);
    if (validationError) {
      setError(validationError);
      return;
    }

    if (trimmedPassword !== trimmedConfirm) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);

    try {
      await axios.post(
        `${apiBase}/auth/reset-password?token=${token}`,
        { password: trimmedPassword }
      );

      navigate("/login");
    } catch (err) {
      const status = err.response?.status;
      const data = err.response?.data;

      // 400 – Validation
      if (status === 400 && data?.details?.length) {
        setError(data.details[0]);
        return;
      }

      // 401 – Token expired / invalid
      if (status === 401) {
        setIsValidToken(false);

        const msg = data?.message || "Invalid reset link.";
        setTokenError(msg);

        if (msg.toLowerCase().includes("expired")) {
          setTokenTitle("Expired Link");
        } else {
          setTokenTitle("Invalid Link");
        }
        return;
      }

      // 500 – Server error
      if (status === 500) {
        setError("Something went wrong. Please try again later.");
        return;
      }

      setError("Unable to reset password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  /* ------------------------------------------------
     UI STATES
  ------------------------------------------------ */

  // Loading
  if (isValidToken === null) {
    return (
      <div className="login-page">
        <div className="login-card">
          <h2 className="login-title">Checking Link...</h2>
          <p>Please wait.</p>
        </div>
      </div>
    );
  }

  // Invalid / Expired token
  if (isValidToken === false) {
    return (
      <div className="login-page">
        <div className="login-card">
          <h2 className="login-title">{tokenTitle}</h2>
          <p className="error-text">{tokenError}</p>
          <button className="login-btn" onClick={() => navigate("/login")}>
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  /* ------------------------------------------------
     RESET PASSWORD FORM
  ------------------------------------------------ */
  return (
    <div className="login-page">
      <div className="login-left">
        <img src={LoginImage} alt="Reset visual" className="login-visual" />
      </div>

      <div className="login-card">
        <div className="logo-box-wrapper">
          <div className="logo-box">
            <img src={Logo} className="login-logo" alt="logo" />
          </div>
        </div>

        <h2 className="login-title">Reset Password</h2>

        <form onSubmit={handleSubmit}>
          {/* NEW PASSWORD */}
          <div className="input-group">
            <input
              type={showNewPassword ? "text" : "password"}
              className="login-input"
              placeholder="New Password"
              value={newPassword}
              onChange={(e) => {
                const value = e.target.value;
                setNewPassword(value);

                const errMsg = validatePassword(value);
                setPasswordError(errMsg);
                setStrength(getPasswordStrength(value));
              }}
            />
            <img
              src={EyeIcon}
              alt="toggle password"
              className="eye-icon"
              onClick={() => setShowNewPassword(!showNewPassword)}
            />
          </div>

          {/* Strength Meter */}
          <div className="upp-strength-bar">
            <span className={`bar ${strength === "weak" ? "active-weak" : ""}`} />
            <span className={`bar ${strength === "medium" ? "active-medium" : ""}`} />
            <span className={`bar ${strength === "strong" ? "active-strong" : ""}`} />
          </div>

          {/* Error message BELOW the meter */}
          {passwordError && (
            <p className="upp-field-error password-error">
              {passwordError}
            </p>
          )}



          {/* CONFIRM PASSWORD */}
          <div className="input-group">
            <input
              type={showConfirmPassword ? "text" : "password"}
              className="login-input"
              placeholder="Confirm Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
            <img
              src={EyeIcon}
              alt="toggle password"
              className="eye-icon"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            />
          </div>

          {error && <p className="error-text">{error}</p>}

          <button className="login-btn" type="submit" disabled={loading}>
            {loading ? "Saving..." : "Reset Password"}
          </button>
        </form>

        <p
          className="forgot-password back-login"
          onClick={() => navigate("/login")}
        >
          Back to Login
        </p>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
